import {
  AppCard,
  DeleteButtonWithModalConfirmation,
  Icon,
  InfoField,
  PageContainer,
  RouteButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiAccount,
  mdiClockPlusOutline,
  mdiCogOutline,
  mdiFileDocumentOutline,
  mdiHistory,
  mdiPencil,
  mdiUpdate,
} from "@mdi/js";
import { Box, Chip, Grid } from "@mui/material";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { UserInfoBadge } from "../../../../components/molecules/UserInfoBadge";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { useAuth } from "../../../../hooks/useAuth";
import {
  deleteAssignmentRule,
  fetchAssignmentRule,
} from "../../../../services/assignmentRules";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";

const loaderQueryOptions = (assignmentRuleId: string) =>
  queryOptions({
    queryFn: () => fetchAssignmentRule(assignmentRuleId),
    queryKey: ["mmRequestAssignmentRules", "detail", assignmentRuleId],
    staleTime: 1000 * 60 * 5,
  });

const ShowMmRequestAssignmentRule: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { assignmentRuleId } = Route.useParams();
  const { data: assignmentRule } = useSuspenseQuery(
    loaderQueryOptions(assignmentRuleId),
  );

  const createdAt = t(
    "intlDateTime",
    i18nextParseToDate(assignmentRule.created_at),
  );
  const updatedAt = t(
    "intlDateTime",
    i18nextParseToDate(assignmentRule.updated_at),
  );

  return (
    <PageContainer showBackButton title={t("assignmentRules.titles.show")}>
      <Grid
        container
        spacing={3}
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 3,
          width: "100%",
        }}
      >
        <Grid size={8}>
          <AppCard
            icon={<Icon icon={mdiFileDocumentOutline} />}
            subtitle={t("assignmentRules.titles.detailsSubtitle")}
            title={t("common.details")}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                width: "100%",
              }}
            >
              <InfoField label={t("assignmentRules.labels.user")}>
                <UserInfoBadge
                  avatarIcon={<Icon icon={mdiAccount} />}
                  avatarType="icon"
                  info={assignmentRule.user.full_name}
                  photoUser={assignmentRule.user}
                  subInfo={assignmentRule.user.login}
                />
              </InfoField>

              <InfoField label={t("assignmentRules.labels.mmRequestReasons")}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {assignmentRule.mm_request_reasons?.length > 0 ? (
                    assignmentRule.mm_request_reasons.map((reason) => (
                      <Chip
                        key={reason.mm_request_reason.id}
                        label={reason.mm_request_reason.title}
                        size="small"
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Chip label="-" size="small" variant="outlined" />
                  )}
                </Box>
              </InfoField>

              <InfoField label={t("assignmentRules.labels.plantFields")}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {assignmentRule.plant_fields?.length > 0 ? (
                    assignmentRule.plant_fields.map((plantField) => (
                      <Chip
                        key={plantField.plant_field.id}
                        label={plantField.plant_field.name}
                        size="small"
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Chip label="-" size="small" variant="outlined" />
                  )}
                </Box>
              </InfoField>

              <InfoField label={t("assignmentRules.labels.requesterAreas")}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {assignmentRule.requester_areas?.length > 0 ? (
                    assignmentRule.requester_areas.map((area) => (
                      <Chip
                        key={area.requester_area.id}
                        label={area.requester_area.name}
                        size="small"
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Chip label="-" size="small" variant="outlined" />
                  )}
                </Box>
              </InfoField>

              <InfoField label={t("assignmentRules.labels.plants")}>
                {assignmentRule.plants?.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}
                  >
                    {assignmentRule.plants.map((plant) => (
                      <Chip
                        key={plant.plant.id}
                        label={plant.plant.plant_number_with_name}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                ) : null}
              </InfoField>
            </Box>
          </AppCard>
        </Grid>
        <Grid
          size={4}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <AppCard
            icon={<Icon icon={mdiHistory} />}
            title={t("common.history")}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <InfoField
                label={t("common.createdAt")}
                labelIconPath={mdiClockPlusOutline}
                text={createdAt}
              />
              <InfoField
                label={t("common.updatedAt")}
                labelIconPath={mdiUpdate}
                text={updatedAt}
              />
            </Box>
          </AppCard>
          {user?.roles_names?.includes("Assignment Rules - Update") ||
          user?.roles_names?.includes("Assignment Rules - Delete") ? (
            <AppCard
              icon={<Icon icon={mdiCogOutline} />}
              title={t("common.actions")}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {user?.roles_names?.includes("Assignment Rules - Update") && (
                  <RouteButtonLink
                    params={{ assignmentRuleId }}
                    startIcon={<Icon icon={mdiPencil} />}
                    to="/mm_request_assignment_rules/$assignmentRuleId/edit"
                  >
                    {t("common.edit")}
                  </RouteButtonLink>
                )}
                {user?.roles_names?.includes("Assignment Rules - Delete") && (
                  <DeleteButtonWithModalConfirmation
                    dialogContent={t(
                      "assignmentRules.messages.confirmationDialog",
                    )}
                    dialogTitle={t("assignmentRules.titles.deleteDialog")}
                    errorMessage={t("assignmentRules.messages.deleteError")}
                    invalidateQueryKey={["mmRequestAssignmentRules"]}
                    itemId={assignmentRuleId}
                    onDelete={deleteAssignmentRule}
                    onSuccess={() =>
                      navigate({ to: "/mm_request_assignment_rules" })
                    }
                    successMessage={t("assignmentRules.messages.deleteSuccess")}
                  />
                )}
              </Box>
            </AppCard>
          ) : null}
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/mm_request_assignment_rules/$assignmentRuleId/",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Assignment Rules - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: ShowMmRequestAssignmentRule,
  loader: ({ context, params: { assignmentRuleId } }) =>
    context.queryClient.ensureQueryData(loaderQueryOptions(assignmentRuleId)),
});
